import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { useUserRoleLevel } from "@/hooks/useUserRoleLevel";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";

/**
 * Props for the RoleProtectedRoute component.
 */
interface RoleProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Array of allowed role levels (2, 4, 5, 6, or 8)
   * User must have one of these levels to access the route
   */
  allowedLevels: number[];
}

/**
 * Route protection component that requires authentication and specific role levels.
 * 
 * Features:
 * - Checks if user is authenticated before checking role level
 * - Verifies user has one of the allowed role levels
 * - Subscribes to authentication state changes
 * - Shows loading state while checking authentication and role
 * - Redirects to /auth if user is not authenticated
 * - Redirects to home page with error message if user doesn't have required role level
 * 
 * This component wraps routes that require specific user role levels.
 * It ensures only users with the specified role levels can access protected pages.
 */
export const RoleProtectedRoute = ({ children, allowedLevels }: RoleProtectedRouteProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { roleLevel, loading: roleLoading } = useUserRoleLevel();

  /**
   * Effect: Sets up authentication state monitoring.
   * 
   * Process:
   * 1. Subscribes to auth state changes (sign in, sign out, token refresh)
   * 2. Checks for existing session immediately
   * 3. Updates user state when auth state changes
   * 
   * The listener is set up first to catch any state changes that might occur
   * during the getSession() call.
   */
  useEffect(() => {
    let isMounted = true;
    console.log('[RoleProtectedRoute] Setting up auth state monitoring');

    // Set up auth state listener FIRST to catch any changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[RoleProtectedRoute] Auth state changed:', event, 'User:', session?.user?.id || 'null');
        if (isMounted) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false); // CRITICAL: Set loading to false when auth state changes
        }
      }
    );

    // THEN check for existing session with error handling and timeout
    console.log('[RoleProtectedRoute] Checking for existing session...');
    
    // Add a timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        console.warn('[RoleProtectedRoute] getSession() timeout after 5 seconds, using auth state change result');
        // Don't set loading here - let auth state change handle it
      }
    }, 5000);
    
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        clearTimeout(timeoutId);
        console.log('[RoleProtectedRoute] getSession result - Session:', session ? 'exists' : 'null', 'Error:', error?.message || 'none');
        if (!isMounted) {
          console.log('[RoleProtectedRoute] Component unmounted, ignoring getSession result');
          return;
        }
        
        if (error) {
          console.error('[RoleProtectedRoute] Error getting session:', error);
          
          // Capture session error to Sentry
          Sentry.captureException(error, {
            tags: {
              operation: 'session-check',
              errorType: 'authentication',
            },
            extra: {
              errorCode: (error as any).status || 'unknown',
              errorMessage: error.message,
              timestamp: new Date().toISOString(),
            },
          });
          
          setSession(null);
          setUser(null);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
        // Only set loading to false if it's still true (auth state change might have already set it)
        setLoading((prev) => {
          if (prev) {
            console.log('[RoleProtectedRoute] Loading set to false after getSession');
          }
          return false;
        });
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        console.error('[RoleProtectedRoute] Unexpected error getting session:', error);
        
        // Capture unexpected error to Sentry
        Sentry.captureException(error, {
          tags: {
            operation: 'session-check',
            errorType: 'unexpected',
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        });
        
        if (isMounted) {
          setSession(null);
          setUser(null);
          setLoading((prev) => {
            if (prev) {
              console.log('[RoleProtectedRoute] Loading set to false after error');
            }
            return false;
          });
        }
      });

    return () => {
      console.log('[RoleProtectedRoute] Cleaning up auth state monitoring');
      isMounted = false;
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  /**
   * Effect: Check role level after authentication is confirmed
   * Redirects to home page if user doesn't have required role level
   */
  useEffect(() => {
    // Wait for both auth and role loading to complete
    if (loading || roleLoading) {
      return;
    }

    // If not authenticated, ProtectedRoute will handle redirect
    if (!user) {
      return;
    }

    // Check if user has one of the allowed role levels
    if (!allowedLevels.includes(roleLevel)) {
      console.log('[RoleProtectedRoute] User does not have required role level. User level:', roleLevel, 'Required levels:', allowedLevels);
      toast.error("You don't have permission to access this page");
      navigate('/', { replace: true });
    }
  }, [user, roleLevel, loading, roleLoading, allowedLevels, navigate]);

  console.log('[RoleProtectedRoute] Render - Loading:', loading || roleLoading, 'User:', user?.id || 'null', 'Role Level:', roleLevel, 'Allowed Levels:', allowedLevels);

  // Show loading state while checking authentication or role
  if (loading || roleLoading) {
    console.log('[RoleProtectedRoute] Rendering loading state');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Redirect to auth if not authenticated
  if (!user) {
    console.log('[RoleProtectedRoute] No user, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  // Check if user has required role level
  if (!allowedLevels.includes(roleLevel)) {
    console.log('[RoleProtectedRoute] User does not have required role level, redirecting to home');
    // The useEffect above will handle the redirect and toast, but we need to return something here
    // Return null to prevent rendering children while redirecting
    return null;
  }

  console.log('[RoleProtectedRoute] User authenticated and has required role level, rendering children');
  return <>{children}</>;
};





